import { useEffect, useMemo, useRef, useState } from 'react'
import MapGL, {
  Layer,
  Marker,
  NavigationControl,
  Source,
  type MapRef,
} from 'react-map-gl/mapbox-legacy'
import Supercluster from 'supercluster'
import { LngLatBounds } from 'mapbox-gl'
import { LocateFixed, MapPin, MapPinOff, Route, SlidersHorizontal, X } from 'lucide-react'
import { Button, EmptyState, IconButton, Notice } from '@/components/play'
import { api } from '@/util/api'
import { parseGpx } from '@/lib/comrades/gpx'
import { playTokens } from '@/lib/theme/playTokens'
import { CAMERA_STATE_LABELS, locationLabel, type CameraLocation } from '@/lib/workspace'
import {
  CameraFullView,
  CameraSphere,
  cameraFrame,
  focusCamera,
  formatAge,
  useExpandedCamera,
  type CameraFreshness,
  type CameraSphereMode,
  type ChangeCameraMode,
} from '@/components/map/camera-sphere'
import type { EventRoutesIndexResponse, StreamResource } from '@/types/events'
import { CameraPicture, CameraStatus, useCameraState } from './WorkspaceMedia'
import 'mapbox-gl/dist/mapbox-gl.css'

export type WorkspaceMapView = {
  longitude: number
  latitude: number
  zoom: number
  bearing: number
  pitch: number
}
const WORLD: WorkspaceMapView = {
  longitude: 15,
  latitude: 15,
  zoom: 1.5,
  bearing: 0,
  pitch: 0,
}
const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN
const STALE_MS = 60000
/** The course in Play ink on a white casing, so it reads on the light map without taking the pink of selection. */
const COURSE_INK = playTokens.theme.neutral['neutral-10']
const COURSE_CASING = playTokens.theme.neutral['neutral-1']

/** Fixed pins never go stale; a moving camera is stale a minute after its last fix. */
function cameraFreshness(location: CameraLocation, now: number): CameraFreshness {
  const time = Date.parse(location.timestamp || '')
  const known = Number.isFinite(time)
  return {
    label: locationLabel(location, now),
    age: known ? formatAge(now - time) : 'No recent fix',
    stale: !location.fixed && (!known || now - time > STALE_MS),
  }
}

export function WorkspaceMap({
  eventId,
  cameras,
  locations,
  selectedId,
  featuredIdentity,
  onPreview,
  onDetails,
  view,
  onViewChange,
  courseVersion,
  onCourse,
}: {
  eventId: string
  cameras: StreamResource[]
  locations: CameraLocation[]
  selectedId: string | null
  /** The publicly featured participant; independent of selection. */
  featuredIdentity?: string | null
  onPreview: (stream: StreamResource) => void
  onDetails: (stream: StreamResource) => void
  view: WorkspaceMapView | null
  onViewChange: (view: WorkspaceMapView) => void
  courseVersion: number
  onCourse: () => void
}) {
  const map = useRef<MapRef>(null)
  const stage = useRef<HTMLDivElement>(null)
  const fitted = useRef(view !== null)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState(false)
  const [course, setCourse] = useState<ReturnType<typeof parseGpx> | null>(null)
  const [courseError, setCourseError] = useState(false)
  const [clusterIds, setClusterIds] = useState<string[]>([])
  const [now, setNow] = useState(Date.now())
  const currentView = view || WORLD
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15000)
    return () => clearInterval(timer)
  }, [])
  useEffect(() => {
    const controller = new AbortController()
    setCourse(null)
    setCourseError(false)
    void api
      .get<EventRoutesIndexResponse>(`/events/${eventId}/routes`)
      .then(async (response) => {
        const route =
          response.data.find((item) => item.primary) || response.data[0]
        if (!route || controller.signal.aborted) return
        const responseGpx = await fetch(
          route.gpx_url ||
            `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1'}/public/events/${eventId}/routes/${route.id}/gpx`,
          { signal: controller.signal },
        )
        if (!responseGpx.ok) throw new Error('course_unavailable')
        const parsed = parseGpx(await responseGpx.text())
        if (!parsed.points.length) throw new Error('course_empty')
        if (!controller.signal.aborted) setCourse(parsed)
      })
      .catch(() => {
        if (!controller.signal.aborted) setCourseError(true)
      })
    return () => controller.abort()
  }, [eventId, courseVersion])

  const index = useMemo(
    () =>
      new Supercluster<{ cameraId: string }>({ radius: 72, maxZoom: 18 }).load(
        locations.map((location) => ({
          type: 'Feature',
          properties: { cameraId: location.stream.id },
          geometry: {
            type: 'Point',
            coordinates: [location.longitude, location.latitude],
          },
        })),
      ),
    [locations],
  )
  const clusters = useMemo(() => {
    const bounds = map.current?.getBounds()
    return index.getClusters(
      bounds
        ? [
            bounds.getWest(),
            bounds.getSouth(),
            bounds.getEast(),
            bounds.getNorth(),
          ]
        : [-180, -85, 180, 85],
      Math.floor(currentView.zoom),
    )
  }, [index, currentView, ready])
  const byId = useMemo(
    () => new Map(locations.map((location) => [location.stream.id, location])),
    [locations],
  )
  const { expanded, modeOf, change } = useExpandedCamera(
    locations.map((location) => location.stream.id),
  )
  const full = expanded?.mode === 'full' ? byId.get(expanded.id) : undefined
  const isFeatured = (stream: StreamResource) =>
    !!featuredIdentity &&
    featuredIdentity === stream.attributes.participant_identity
  function fit() {
    if (!map.current) return
    const bounds = new LngLatBounds()
    if (course?.bounds) {
      bounds.extend(course.bounds.getSouthWest())
      bounds.extend(course.bounds.getNorthEast())
    }
    locations.forEach((location) =>
      bounds.extend([location.longitude, location.latitude]),
    )
    if (!bounds.isEmpty())
      map.current.fitBounds(bounds, {
        padding: 90,
        maxZoom: 14,
        duration: matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 0
          : 450,
      })
  }
  useEffect(() => {
    if (!ready || fitted.current || (!locations.length && !course)) return
    fit()
    fitted.current = true
  }, [ready, locations, course])

  return (
    <div className="pw-map-layout">
      <div className="pw-map" ref={stage}>
        {TOKEN && !error ? (
          <MapGL
            ref={map}
            {...currentView}
            onMove={(event) => onViewChange(event.viewState)}
            onLoad={() => setReady(true)}
            onError={() => setError(true)}
            onClick={(event) => {
              // Marker clicks bubble through the map; only a click on the map itself puts a preview away.
              const target = event.originalEvent?.target as Element | null
              if (target?.closest?.('.cs-sphere')) return
              if (expanded?.mode === 'peek')
                change(expanded.id, 'orb', ['peek'])
            }}
            mapboxAccessToken={TOKEN}
            mapStyle="mapbox://styles/mapbox/light-v11"
            minZoom={1}
            maxZoom={20}
            attributionControl={true}
            reuseMaps={false}
          >
            <NavigationControl position="bottom-right" showCompass={false} />
            {course && (
              <Source
                id="event-course"
                type="geojson"
                data={course.lineFeature}
              >
                <Layer
                  id="event-course-casing"
                  type="line"
                  layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                  paint={{ 'line-color': COURSE_CASING, 'line-width': 7 }}
                />
                <Layer
                  id="event-course-line"
                  type="line"
                  layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                  paint={{ 'line-color': COURSE_INK, 'line-width': 4 }}
                />
              </Source>
            )}
            {clusters.map((feature) => {
              const [longitude, latitude] = feature.geometry.coordinates
              if (
                'cluster' in feature.properties &&
                feature.properties.cluster
              ) {
                const id = feature.properties.cluster_id
                const count = feature.properties.point_count
                return (
                  <Marker
                    key={`cluster-${id}`}
                    longitude={longitude}
                    latitude={latitude}
                    anchor="center"
                  >
                    <button
                      type="button"
                      className="pw-map-cluster"
                      aria-label={`Show ${count} cameras`}
                      onClick={() => {
                        if (currentView.zoom < 17)
                          map.current?.easeTo({
                            center: [longitude, latitude],
                            zoom: Math.min(
                              index.getClusterExpansionZoom(id),
                              18,
                            ),
                            duration: matchMedia(
                              '(prefers-reduced-motion: reduce)',
                            ).matches
                              ? 0
                              : 400,
                          })
                        else
                          setClusterIds(
                            index
                              .getLeaves(id, Infinity)
                              .map((leaf) => leaf.properties.cameraId),
                          )
                      }}
                    >
                      <span>{count}</span>
                      <small>cameras</small>
                    </button>
                  </Marker>
                )
              }
              const location = byId.get(feature.properties.cameraId)
              if (!location) return null
              return (
                <Marker
                  key={location.stream.id}
                  longitude={longitude}
                  latitude={latitude}
                  anchor="bottom"
                  style={{
                    zIndex: modeOf(location.stream.id) === 'orb' ? 1 : 2,
                  }}
                >
                  <MapCamera
                    location={location}
                    selected={selectedId === location.stream.id}
                    featured={isFeatured(location.stream)}
                    now={now}
                    mode={modeOf(location.stream.id)}
                    onModeChange={change}
                  />
                </Marker>
              )
            })}
          </MapGL>
        ) : (
          <EmptyState
            className="pw-map-unavailable"
            icon={<MapPinOff />}
            title="The map is unavailable"
            description="Your cameras are still in the list."
            action={error && (
              <Button
                variant="secondary"
                onClick={() => {
                  setError(false)
                  setReady(false)
                }}
              >
                Try the map again
              </Button>
            )}
          />
        )}
        {TOKEN && !error && (
          <div className="pw-map-tools">
            <IconButton
              label="Fit cameras and course"
              variant="ghost"
              disabled={!locations.length && !course}
              onClick={fit}
            >
              <LocateFixed />
            </IconButton>
            <IconButton
              label="Add or edit course"
              variant="ghost"
              onClick={onCourse}
            >
              <Route />
            </IconButton>
          </div>
        )}
        {TOKEN && !error && !locations.length && (
          <p className="pw-map-note">
            Cameras appear here when they share a location. You can also pin a
            fixed camera.
          </p>
        )}
        {courseError && (
          <Notice
            tone="error"
            className="pw-map-course-error"
            action={<Button variant="secondary" size="compact" onClick={onCourse}>Check course</Button>}
          >
            The course couldn’t load.
          </Notice>
        )}
        {full && (
          <MapCameraFull
            key={full.stream.id}
            location={full}
            now={now}
            source={() => cameraFrame(stage.current, full.stream.id)}
            onDismiss={() => {
              change(full.stream.id, 'peek', ['full'])
              focusCamera(stage.current, full.stream.id)
            }}
            onControls={() => onPreview(full.stream)}
          />
        )}
        {clusterIds.length > 0 && (
          <section className="pw-map-overlap" aria-labelledby="pw-map-overlap-title">
            <div className="pw-map-overlap-heading">
              <h3 id="pw-map-overlap-title">Cameras at this location</h3>
              <IconButton
                label="Close camera list"
                variant="ghost"
                onClick={() => setClusterIds([])}
              >
                <X />
              </IconButton>
            </div>
            {clusterIds.map((id) => {
              const item = byId.get(id)
              return (
                item && (
                  <Button
                    key={id}
                    variant="ghost"
                    className="pw-map-overlap-camera"
                    onClick={() => onPreview(item.stream)}
                  >
                    {item.stream.attributes.name}
                  </Button>
                )
              )
            })}
          </section>
        )}
      </div>
      <aside className="pw-map-rail" aria-label="Cameras on the map">
        <div className="pw-map-rail-title">
          <h2>Cameras</h2>
          <span>{cameras.length}</span>
        </div>
        {cameras.map((camera) => (
          <MapCameraRow
            key={camera.id}
            camera={camera}
            location={byId.get(camera.id)}
            now={now}
            selected={selectedId === camera.id}
            onPreview={() => onPreview(camera)}
            onDetails={() => onDetails(camera)}
          />
        ))}
        {!cameras.length && (
          <p>Add your first camera to bring this map to life.</p>
        )}
      </aside>
    </div>
  )
}

function MapCamera({
  location,
  selected,
  featured,
  now,
  mode,
  onModeChange,
}: {
  location: CameraLocation
  selected: boolean
  featured: boolean
  now: number
  mode: CameraSphereMode
  onModeChange: ChangeCameraMode
}) {
  const state = useCameraState(location.stream)
  return (
    <CameraSphere
      id={location.stream.id}
      name={location.stream.attributes.name}
      video={<CameraPicture stream={location.stream} portal />}
      mode={mode}
      onModeChange={onModeChange}
      live={state === 'live'}
      status={state === 'live' ? undefined : CAMERA_STATE_LABELS[state]}
      freshness={cameraFreshness(location, now)}
      selected={selected}
      featured={featured}
      size="md"
    />
  )
}

function MapCameraFull({
  location,
  now,
  source,
  onDismiss,
  onControls,
}: {
  location: CameraLocation
  now: number
  source: () => Element | null
  onDismiss: () => void
  onControls: () => void
}) {
  const state = useCameraState(location.stream)
  return (
    <CameraFullView
      className="pw-map-full"
      name={location.stream.attributes.name}
      video={<CameraPicture stream={location.stream} portal />}
      live={state === 'live'}
      status={state === 'live' ? undefined : CAMERA_STATE_LABELS[state]}
      freshness={cameraFreshness(location, now)}
      location={location}
      token={TOKEN}
      source={source}
      onDismiss={onDismiss}
      actions={
        <IconButton label="Camera controls" variant="ghost" elevated onClick={onControls}>
          <SlidersHorizontal aria-hidden />
        </IconButton>
      }
    />
  )
}

function MapCameraRow({
  camera,
  location,
  now,
  selected,
  onPreview,
  onDetails,
}: {
  camera: StreamResource
  location?: CameraLocation
  now: number
  selected: boolean
  onPreview: () => void
  onDetails: () => void
}) {
  const state = useCameraState(camera)
  return (
    <div
      className={`pw-map-camera ${selected ? 'pw-map-camera--selected' : ''}`}
    >
      <button
        type="button"
        className="pw-map-camera-preview"
        onClick={onPreview}
        aria-label={`Preview ${camera.attributes.name}`}
      >
        <span className="pw-map-camera-picture">
          <CameraPicture stream={camera} portal />
        </span>
        <span>
          <strong>{camera.attributes.name}</strong>
          <CameraStatus state={state} />
          <small>{locationLabel(location, now)}</small>
        </span>
      </button>
      {!location && (
        <Button
          variant="ghost"
          size="compact"
          className="pw-map-camera-locate"
          onClick={onDetails}
        >
          <MapPin aria-hidden="true" />
          Set location
        </Button>
      )}
    </div>
  )
}
